<!--
  @fileoverview Message Bubble Component - Text Message Renderer

  Renders text-based chat messages with full formatting support including:
  - Markdown rendering (via markHTML filter)
  - Read more/less truncation for long messages
  - Timestamps with sender attribution
  - Inline buttons for bot responses
  - Dropdown selections
  - Learning feedback (thumbs up/down)
  - Gen AI citations (sources for AI responses)
  - Message resend functionality for failed user messages

  @component MessageBubble
  @displayName Text Message Bubble

  Features:
  - Left-aligned bubbles for bot/agent messages (blue default)
  - Right-aligned bubbles for user messages (gray default)
  - iOS-style speech bubble tails
  - RTL layout support
  - Video expand button for embedded videos
  - Configurable colors via CSS custom properties

  @example
  <MessageBubble
    :message="messageObject"
    :is-user-message="false"
    :button-outline="'primary'"
    :agents="agentsList"
    :should-disable-if-in-livechat="false"
    @sendPostback="handlePostback"
    @handleExpandVideo="expandVideo"
  />
-->
<template>
  <!-- Wrapper: only renders if message has text content -->
  <div
    v-if="text"
    class="bubble-wrapper"
    :class="{
      'bubble-wrapper--user': isUserMessage,
      'bubble-wrapper--agent': !isUserMessage,
    }"
  >
    <div
      class="message-wrapper"
      :class="{
        'message-bubble-left': !isUserMessage,
        'message-bubble-right': isUserMessage,
      }"
    >
      <p class="!mb-0 break-words" v-html="markedText"></p>
      <div
        v-if="
          !isUserMessage &&
          cleanResult.length > settings.readMoreConfigure.readMoreCharacterCount &&
          !shouldShowFullContent &&
          settings.readMoreConfigure.enabled
        "
        class="keyreply-read-more"
        @click="shouldShowFullContent = true"
      >
        Read more
      </div>
      <template v-if="hasSelection">
        <DropdownSelection
          :selections="message.data.selections"
          :button-outline="buttonOutline"
          :should-disable-if-in-livechat="shouldDisableIfInLivechat"
          @sendPostback="$emit('sendPostback', $event)"
        />
      </template>
      <template v-if="hasButton">
        <br />
        <MessageButtons
          :message="message"
          :button-outline="buttonOutline"
          :should-disable-if-in-livechat="shouldDisableIfInLivechat"
          @sendPostback="$emit('sendPostback', $event)"
        />
      </template>
      <template v-if="hasLearningFeedback">
        <MessageFeedbacks :message="message" @sendPostback="$emit('sendPostback', $event)" />
      </template>
      <template v-if="hasCitations">
        <GenAICitations :citations="citations" />
      </template>

      <div class="text-right">
        <i
          v-html="timestamp(message)"
          class="bubble-timestamp"
          :class="{
            'bubble-timestamp--user': isUserMessage,
            'bubble-timestamp--agent': !isUserMessage,
          }"
          v-if="!isUiBetaEnabled"
        ></i>
        <span
          v-else
          class="bubble-timestamp"
          :class="{
            'bubble-timestamp--user': isUserMessage,
            'bubble-timestamp--agent': !isUserMessage,
          }"
          v-html="timestamp(message)"
        >
        </span>
      </div>
    </div>
    <ResendMessageButton
      :isVisible="isUserMessage && !isMessageSent"
      :resendMessage="resendMessage"
      :isDisabled="isResendButtonDisabled"
      :errorMessage="message.errorMessage"
    />
    <div class="btn-video-expand" @click="$emit('handleExpandVideo', message)" v-if="isVideo">
      <el-icon><VideoPlay /></el-icon>
    </div>
  </div>
</template>

<script setup>
/**
 * @module components/ChatWindow/Message/Bubble
 * @description Text message bubble with markdown rendering and interactive elements
 */
import { ref, computed, watch } from "vue";
import * as _ from "lodash-es";
import MessageButtons from "./Buttons";
import MessageFeedbacks from "./Feedbacks";
import ResendMessageButton from "./ResendMessageButton";
import DropdownSelection from "./DropdownSelection";
import dayjs from "dayjs";
import GenAICitations from "./GenAICitations";
import { useMainStore } from "@/stores/main";
import { markHTML } from "@/filters";
import { VideoPlay } from "@element-plus/icons-vue";

/**
 * Component props
 */
const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
  buttonOutline: {
    type: String,
    default: "",
  },
  isUserMessage: {
    type: Boolean,
    default: false,
  },
  agents: {
    type: Array,
    default: () => [],
  },
  shouldDisableIfInLivechat: {
    type: Boolean,
    default: false,
  },
});

/**
 * Component emits
 */
defineEmits(["sendPostback", "handleExpandVideo"]);

/**
 * Pinia store instance
 */
const store = useMainStore();

/**
 * Component local state
 */
/** Whether resend button is temporarily disabled (prevents spam) */
const isResendButtonDisabled = ref(false);
/** Whether the message was successfully sent (for user messages) */
const isMessageSent = ref(true);
/** Whether to show full content (vs truncated with "Read more") */
const shouldShowFullContent = ref(false);

/**
 * Access webchat settings from Pinia store
 */
const settings = computed(() => store.settings);

/**
 * Check if UI Beta features are enabled
 */
const isUiBetaEnabled = computed(() => store.isUiBetaEnabled);

/**
 * Check if string contains only whitespace characters
 *
 * Used to validate sender/organization names are meaningful.
 *
 * @param {string} str - String to check
 * @returns {boolean} True if string is only whitespace
 */
function isOnlyWhiteSpace(str) {
  return !str.replace(/\s/g, "").length;
}

/**
 * Get the sender name from message metadata
 *
 * For bot messages, extracts the agent/sender name from data.meta.name.
 * User messages return null.
 */
const sender = computed(() => {
  const senderName = _.get(props.message, "data.meta.name", null);
  return props.isUserMessage ? null : senderName;
});

/**
 * Build reply attribution string for bot/agent messages
 *
 * Constructs a string like "Bot from Organization" or "Agent Name from Org"
 * based on various configuration options and chat state.
 *
 * Logic:
 * 1. If multiple agents in chat, show individual agent names (bold)
 * 2. If single agent with showAgentName, show agent name
 * 3. Otherwise show bot name from settings
 * 4. Append organization name if both names are valid
 */
const replyName = computed(() => {
  const botName = store.settings.name || "";
  const botNameValid = botName && !isOnlyWhiteSpace(botName);
  const organizationName = !botNameValid ? "" : store.settings.organizationName || "";

  const forceShowAgentName = props.agents.length === 1 && store.settings.showAgentName;
  const hasMoreThanOneAgentInChat = props.agents.length > 1;
  const fromBot = props.message.type === "reply";
  const isValidName = !isOnlyWhiteSpace(botName) && !isOnlyWhiteSpace(organizationName);

  const prefix = (valid, senderVal) => (valid ? `${senderVal} from ` : senderVal);

  const agentName = hasMoreThanOneAgentInChat ? `<strong>${sender.value}</strong>` : sender.value;
  const botReply = organizationName ? prefix(isValidName, botName) : botName;
  const agentOrBotName = sender.value ? prefix(isValidName, agentName) : botReply;
  const showAgent = fromBot || forceShowAgentName || hasMoreThanOneAgentInChat;
  const replyNameResult = `${showAgent ? agentOrBotName : ""}${organizationName}`;
  return replyNameResult;
});

/**
 * Generate timestamp string with sender attribution
 *
 * Format: "You at 15 Jan, 10:30am" or "Bot from Org at 15 Jan, 10:30am"
 *
 * @param {Object} message - Message object with timestamp
 * @returns {string} Formatted timestamp string
 */
function timestamp(message) {
  const source = props.isUserMessage ? "You" : replyName.value;
  return `${source} at ${dayjs(message.timestamp).format("D MMM, hh:mma")}`;
}

/**
 * Extract text content from message
 *
 * Checks multiple possible locations for text:
 * 1. trendingText (for trending topic messages)
 * 2. data.content[0].text (standard text)
 * 3. data.content[0].postbackText (postback display text)
 * 4. data.content[0].caption (media captions)
 */
const text = computed(() => {
  const textVal = _.get(props.message, "data.content[0].text", null);
  const postbackText = _.get(props.message, "data.content[0].postbackText", null);
  const captionText = _.get(props.message, "data.content[0].caption", null);
  const trendingText = props.message.trendingText;
  return trendingText ? trendingText : textVal || postbackText || captionText;
});

/**
 * Render text with Markdown processing and optional truncation
 *
 * Uses the markHTML function to convert Markdown to safe HTML.
 * Applies "Read more" truncation for long bot messages when enabled.
 */
const markedText = computed(() => {
  let result = markHTML(text.value, settings.value.userTextColor);
  // Remove html tags, markdown result
  const cleanResultVal = result.replace(/<[^>]*>/g, "");
  if (
    !shouldShowFullContent.value &&
    cleanResultVal.length > settings.value.readMoreConfigure.readMoreCharacterCount &&
    settings.value.readMoreConfigure.enabled &&
    !props.isUserMessage
  ) {
    return result.substring(0, settings.value.readMoreConfigure.readMoreCharacterCount) + "...";
  }
  return result;
});

/**
 * Get plain text version (HTML tags stripped)
 *
 * Used for character count comparisons in "Read more" logic.
 */
const cleanResult = computed(() => {
  return markHTML(text.value, settings.value.userTextColor).replace(/<[^>]*>/g, "");
});

/**
 * Check if message has learning feedback buttons
 *
 * Learning feedback allows users to rate bot responses (thumbs up/down)
 * to improve AI quality.
 */
const hasLearningFeedback = computed(() => {
  return _.get(props.message, "data.learningQuickReplies.length", false);
});

/**
 * Check if message has action buttons
 *
 * Buttons can be postback (send payload) or URL (open link).
 */
const hasButton = computed(() => {
  const buttons = _.get(props.message, "data.content[0].buttons", []);
  return !_.isEmpty(buttons);
});

/**
 * Check if message has dropdown selection
 *
 * Dropdown selections allow multi-select from a list of options.
 */
const hasSelection = computed(() => {
  const selections = _.get(props.message, "data.selections.data", []);
  return !_.isEmpty(selections);
});

/**
 * Check if message contains expandable video
 */
const isVideo = computed(() => {
  return props.message?.data?.content[0]?.options?.mode === "video";
});

/**
 * Check if message has Gen AI citations
 *
 * Citations show sources for AI-generated responses.
 */
const hasCitations = computed(() => {
  const citationsData = props.message?.data?.content[0]?.options?.citations;
  return citationsData && citationsData.data?.length > 0;
});

/**
 * Get citations data for Gen AI responses
 */
const citations = computed(() => {
  return props.message?.data?.content[0]?.options?.citations;
});

/**
 * Update message sent status
 *
 * Called when message send status changes (success/failure).
 *
 * @param {boolean|undefined} status - Send status (true=sent, false=failed)
 */
function updateMessageStatus(status) {
  isMessageSent.value = status || status === undefined;
  isResendButtonDisabled.value = false;
}

/**
 * Resend a failed message
 *
 * Debounced to prevent rapid-fire clicks. Dispatches RESEND_MESSAGE
 * action to retry sending the message.
 */
const resendMessage = _.debounce(
  function () {
    isResendButtonDisabled.value = true;
    store.resendMessage({
      id: props.message.id,
      text: text.value,
      timestamp: props.message.timestamp,
    });
  },
  200,
  { leading: true }
);

/**
 * Watch message for send status changes
 *
 * Updates UI when message transitions from pending to sent/failed.
 */
watch(
  () => props.message,
  (newVal) => {
    updateMessageStatus(newVal.sent);
  }
);
</script>

<style lang="scss">
.message-wrapper {
  display: inline-block;
  margin: 0 16px 10px 16px;

  *:not(button) {
    font-size: 16px !important;
  }

  .bubble-timestamp {
    font-size: 10px !important;
  }

  p {
    margin-top: 0;
    line-height: 1.2;
    padding-bottom: 0;
    &:last-child {
      margin-bottom: 0 !important;
    }
  }

  ul,
  ol {
    padding: revert;
  }

  li {
    margin-top: 0;
    margin-bottom: 5px;
    margin-left: 2px;
  }

  a {
    text-decoration: underline !important;
  }

  a,
  a:visited,
  p,
  li {
    color: inherit;
  }

  img {
    border-radius: 15px;
  }
}

.message-bubble-status {
  font-size: 16px;
  position: absolute;
  right: 2px;
  bottom: 3px;
  z-index: 10;
  font-weight: bold;
}

.message-bubble-left {
  position: relative;
  padding: 15px;
  background: #0b93f6;
  border-radius: 15px;
  color: white;
  text-align: left;
  max-width: 70%;

  &:before {
    content: "";
    position: absolute;
    z-index: 2;
    bottom: -2px;
    left: -7px;
    height: 20px;
    border-bottom-right-radius: 16px 14px;
    -webkit-transform: translate(0, -2px);
    border-left: 20px solid var(--left-bubble-color);
  }
  &:after {
    content: "";
    position: absolute;
    z-index: 3;
    bottom: -2px;
    left: 4px;
    width: 26px;
    height: 20px;
    background: var(--background);
    background-repeat: no-repeat;
    border-bottom-right-radius: 10px;
    -webkit-transform: translate(-30px, -2px);
  }
}

.message-bubble-right {
  position: relative;
  padding: 15px;
  color: black;
  background: #e5e5ea;
  max-width: 70%;
  border-radius: 15px;
  text-align: left;

  &:before {
    content: "";
    position: absolute;
    z-index: -1;
    bottom: -2px;
    right: -7px;
    height: 20px;
    border-bottom-left-radius: 16px 14px;
    border-right: 20px solid var(--right-bubble-color);
    -webkit-transform: translate(0, -2px);
  }
  &:after {
    content: "";
    position: absolute;
    z-index: 1;
    bottom: -2px;
    right: -56px;
    width: 26px;
    height: 20px;
    background: var(--background);
    background-repeat: no-repeat;
    border-bottom-left-radius: 10px;
    -webkit-transform: translate(-30px, -2px);
  }
}

.bubble-wrapper {
  display: flex;
  align-items: center;
  margin-top: 15px;
}

.bubble-wrapper--user {
  justify-content: flex-end;
}

.bubble-wrapper--agent {
  justify-content: flex-start;
}

.message-wrapper {
  font-family: var(--keyreply-font-family);
}

.message-bubble-left {
  color: var(--keyreply-agent-text-color) !important;
  background: var(--keyreply-agent-bubble-color);
  --left-bubble-color: var(--keyreply-agent-bubble-color);
  --background: white;
}

.message-bubble-right {
  color: var(--keyreply-user-text-color) !important;
  background: var(--keyreply-user-bubble-color);
  --right-bubble-color: var(--keyreply-user-bubble-color);
  --background: white;
}

.keyreply-read-more {
  color: var(--keyreply-agent-text-color);
  font-weight: 600;
  font-style: italic;
}

.bubble-timestamp--user {
  color: var(--keyreply-user-text-color);
}

.bubble-timestamp--agent {
  color: var(--keyreply-agent-text-color);
}
</style>

<style lang="scss">
.ui-beta {
  .message-wrapper {
    li {
      margin-left: 0;
    }
  }

  .message-bubble-status {
    right: 0;
  }

  .message-bubble-left {
    font-style: "Avenir Next";
    padding: 16px;
    max-width: 70%;
    min-width: 45%;

    &:before {
      content: none;
    }
    &:after {
      content: none;
    }
  }

  .message-bubble-right {
    padding: 16px;

    &:before {
      content: none;
    }
    &:after {
      content: none;
    }
  }
}

[dir="ltr"] .ui-beta .message-bubble-left {
  border-radius: 16px 16px 16px 0;
}

[dir="rtl"] .ui-beta .message-bubble-left {
  border-radius: 16px 16px 0 16px;
}

[dir="ltr"] .ui-beta .message-bubble-right {
  border-radius: 16px 16px 0 16px;
}

[dir="rtl"] .ui-beta .message-bubble-right {
  border-radius: 16px 16px 16px 0;
}
</style>

<style lang="scss">
[dir="ltr"] .message-wrapper {
  li {
    margin-left: 2px;
  }
}

[dir="rtl"] .message-wrapper {
  li {
    margin-right: 2px;
  }
}

[dir="ltr"] .message-bubble-status {
  right: 2px;
}

[dir="rtl"] .message-bubble-status {
  left: 2px;
}

[dir="ltr"] .message-bubble-left {
  text-align: left;
}

[dir="rtl"] .message-bubble-left {
  text-align: right;
}

[dir="ltr"] .message-bubble-right {
  text-align: left;
}

[dir="rtl"] .message-bubble-right {
  text-align: right;
}
.btn-video-expand {
  margin-left: -10px;
  padding: 5px;
  background: #4e6cce;
  clip-path: circle();
  cursor: pointer;
  i {
    font-size: 30px;
    color: #fff;
  }
}
</style>
